import React from 'react';
import './MemorialPage.css';
import logo from '../../images/logo-horizontal/G2G_black_horizontal_transparent-01.png';

const MemorialPage = () => {
  const teamMembers = [
    {
      name: 'Steven',
      role: 'Co-Founder',
      bio: 'MBA student turned entrepreneur. A veteran with a passion for building things—both with code and power tools.',
    },
    {
      name: 'Gavin',
      role: 'Co-Founder',
      bio: 'A veteran and outdoorsman at heart who loves skiing, climbing, and camping. Dreamed up Gear2Go while figuring out how to better connect with his community.',
    },
    {
      name: 'Jeremy',
      role: 'Developer',
      bio: 'Full-stack developer and coding instructor. When not writing code, you can find him climbing, running trails, or paddling a kayak.',
    },
    {
      name: 'Matt',
      role: 'Developer',
      bio: 'Career changer who found his calling in software development. Passionate about fitness, music, and helping others learn to code.',
    },
  ];

  const values = [
    {
      title: 'Community',
      icon: '🤝',
      description:
        'By connecting like-minded neighbors, we hoped to create enduring bonds that outlast transactional relationships. Shared interests build connections.',
    },
    {
      title: 'Affordability & Access',
      icon: '🔓',
      description:
        'We believed money should never prove an insurmountable barrier for achieving your goals—whether it be kayaking or woodworking.',
    },
    {
      title: 'Sustainability',
      icon: '🌱',
      description:
        'By promoting a "rent first" mentality, we believed Gear2Go could take a lasting step towards creating a more sustainable economy.',
    },
  ];

  const regions = [
    { name: 'Stanford University', emoji: '🎓' },
    { name: 'Nashville', emoji: '🎸' },
    { name: 'Bay Area', emoji: '🌉' },
  ];

  return (
    <div className="memorial-page">
      {/* Hero Section */}
      <section className="memorial-hero">
        <div className="hero-content">
          <img src={logo} alt="Gear2Go Logo" className="memorial-logo" />
          <h1 className="memorial-title">The Gear2Go Experiment</h1>
          <p className="memorial-subtitle">2020 - 2022</p>
          <p className="memorial-tagline">
            A peer-to-peer rental platform born from friendship, service, and a
            shared dream of building connected communities.
          </p>
        </div>
        <div className="hero-gradient"></div>
      </section>

      {/* Story Section */}
      <section className="memorial-section story-section">
        <div className="section-content">
          <h2>Our Story</h2>
          <div className="story-text">
            <p>
              Gear2Go began as a dream shared between MBA students and coding
              bootcamp graduates—friends from different walks of life united by
              a common vision. While some of us were navigating the halls of
              business school, others were transitioning careers and learning to
              code.
            </p>
            <p>
              We imagined a world where your neighbor's camping gear didn't sit
              idle in a garage, where someone curious about kayaking didn't need
              to spend hundreds of dollars to try it, and where communities were
              strengthened through the simple act of sharing.
            </p>
            <p>
              <strong>Was it a mess?</strong> Absolutely. But we learned so
              much—about building software, about entrepreneurship, about
              ourselves. And we're proud of what we created together.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="memorial-section values-section">
        <div className="section-content">
          <h2>What We Believed In</h2>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <span className="value-icon">{value.icon}</span>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regions Section */}
      <section className="memorial-section regions-section">
        <div className="section-content">
          <h2>Where We Launched</h2>
          <p className="regions-intro">
            We tested Gear2Go in three unique communities, each bringing their
            own character to the platform:
          </p>
          <div className="regions-grid">
            {regions.map((region, index) => (
              <div key={index} className="region-card">
                <span className="region-emoji">{region.emoji}</span>
                <h3>{region.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="memorial-section gallery-section">
        <div className="section-content">
          <h2>Memories from the Platform</h2>
          <p className="gallery-intro">
            From kayaks to camping gear, power tools to coolers—these are some
            of the items our community shared with each other.
          </p>
          <div className="screenshot-grid">
            <div className="screenshot-item">
              <img
                src="/screenshots/image-1766787450278.png"
                alt="Stanford University Gear2Go browse page"
              />
            </div>
            <div className="screenshot-item">
              <img
                src="/screenshots/image-1766787470621.png"
                alt="Nashville Gear2Go browse page"
              />
            </div>
            <div className="screenshot-item">
              <img
                src="/screenshots/image-1766787628524.png"
                alt="Bay Area Gear2Go browse page"
              />
            </div>
            <div className="screenshot-item">
              <img
                src="/screenshots/image-1766787554571.png"
                alt="Pelican Challenger Kayak listing detail"
              />
            </div>
            <div className="screenshot-item">
              <img
                src="/screenshots/image-1766787591912.png"
                alt="Milwaukee battery powered circular saw listing"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Built Section */}
      <section className="memorial-section features-section">
        <div className="section-content">
          <h2>What We Built</h2>
          <div className="features-grid">
            <div className="feature-item">
              <span className="feature-icon">🔍</span>
              <h4>Search & Discovery</h4>
              <p>Find gear by category, location, or keyword</p>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📍</span>
              <h4>Location-Based</h4>
              <p>Connect with lenders in your community</p>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💬</span>
              <h4>Messaging</h4>
              <p>Direct communication between renters and lenders</p>
            </div>
            <div className="feature-item">
              <span className="feature-icon">⭐</span>
              <h4>Reviews & Ratings</h4>
              <p>Build trust through community feedback</p>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💳</span>
              <h4>Secure Payments</h4>
              <p>Safe transactions through our platform</p>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📱</span>
              <h4>Mobile-Friendly</h4>
              <p>Rent and lend gear from anywhere</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="memorial-section team-section">
        <div className="section-content">
          <h2>The Team Behind Gear2Go</h2>
          <p className="team-intro">
            Four friends—two entrepreneurs pursuing their MBAs and two
            developers fresh out of coding bootcamp—came together to build
            something meaningful.
          </p>
          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <div key={index} className="team-card">
                <div className="team-avatar">{member.name[0]}</div>
                <h3>{member.name}</h3>
                <span className="team-role">{member.role}</span>
                <p>{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lessons Section */}
      <section className="memorial-section lessons-section">
        <div className="section-content">
          <h2>What We Learned</h2>
          <div className="lessons-content">
            <blockquote>
              "The journey of building Gear2Go taught us more than any classroom
              ever could. We learned to ship imperfect code, to listen to users,
              to pivot when needed, and most importantly—to support each other
              through the chaos of startup life."
            </blockquote>
            <ul className="lessons-list">
              <li>
                Building a two-sided marketplace is <em>hard</em>
              </li>
              <li>Community trust takes time to establish</li>
              <li>
                The best ideas come from personal pain points (like Gavin's many
                Army moves)
              </li>
              <li>Friendship can survive co-founding a startup</li>
              <li>Sometimes the journey matters more than the destination</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="memorial-footer">
        <div className="footer-content">
          <img src={logo} alt="Gear2Go Logo" className="footer-logo" />
          <p>
            Thank you to everyone who believed in our vision and shared their
            gear with neighbors.
          </p>
          <p className="footer-tagline">
            <em>Imagine AirBnb meets the gear space.</em>
          </p>
          <div className="footer-links">
            <a
              href="https://about.gear2go.us/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit Our Original About Page
            </a>
          </div>
          <p className="copyright">© 2020-2022 | Gear2Go</p>
        </div>
      </footer>
    </div>
  );
};

export default MemorialPage;
